/* @jsxImportSource @emotion/react */
import React from 'react'

import { ArticleCtaButton } from './ArticleCtaButton'
import { ArticleShareLinks } from './ArticleShareLinks'
import { ArticleToc } from './ArticleToc'
import {
  ARTICLE_SIDEBAR_BREAKPOINT,
  ARTICLE_STICKY_GAP,
  fontSize,
} from './constants'
import type { ArticleHeading } from './types'

// Short screens give the sticky card's height to the table of contents.
const hideOnShortScreenCss = {
  '@media screen and (max-height: 599px)': { display: 'none' },
} as const

const SECTION_LABEL_CLASS =
  'font-weight-semibold shade-30 margin-top-4 margin-right-4 margin-bottom-8'
const sectionLabelCss = { fontSize: fontSize.small } as const

export function ArticleSidebar(props: {
  header?: React.ReactNode
  headings: ArticleHeading[]
  activeId: string | null
  tocLabel: string
  tocLabelId: string
  shareLabel: string
  shareUrl: string
  shareTitle: string
  shareOnXLabel: string
  shareOnLinkedInLabel: string
  stickyTopOffset: number
  ctaLabel?: string
  ctaHref?: string
}) {
  return (
    <aside
      css={{
        position: 'sticky',
        top: props.stickyTopOffset + ARTICLE_STICKY_GAP,
        alignSelf: 'start',
        [ARTICLE_SIDEBAR_BREAKPOINT]: { position: 'static', width: '100%' },
      }}
    >
      {/* Never taller than the viewport; only the table of contents scrolls. */}
      <div
        className="border-small border-alpha-shade-45 radius-semilarger"
        css={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: `calc(100vh - ${props.stickyTopOffset + 2 * ARTICLE_STICKY_GAP}px)`,
          [ARTICLE_SIDEBAR_BREAKPOINT]: { display: 'block', maxHeight: 'none' },
        }}
      >
        {props.header && (
          <div className="flexbox-no-shrink padding-vertical-20 padding-right-20 padding-left-24 border-bottom-small border-alpha-shade-45">
            {props.header}
          </div>
        )}
        {props.headings.length > 0 && (
          <div
            className="flexbox flexbox-column padding-vertical-20 padding-horizontal-12"
            css={{ minHeight: 0 }}
          >
            <h2
              id={props.tocLabelId}
              className={`${SECTION_LABEL_CLASS} flexbox-no-shrink margin-left-12`}
              css={sectionLabelCss}
            >
              {props.tocLabel}
            </h2>
            <ArticleToc
              headings={props.headings}
              activeId={props.activeId}
              labelledBy={props.tocLabelId}
            />
          </div>
        )}

        <div
          className="flexbox-no-shrink border-top-small border-alpha-shade-45 padding-top-20"
          css={hideOnShortScreenCss}
        >
          <h2
            className={`${SECTION_LABEL_CLASS} margin-left-24`}
            css={sectionLabelCss}
          >
            {props.shareLabel}
          </h2>
          <div className="padding-left-24 padding-top-4 padding-bottom-24">
            <ArticleShareLinks
              shareUrl={props.shareUrl}
              shareTitle={props.shareTitle}
              shareOnXLabel={props.shareOnXLabel}
              shareOnLinkedInLabel={props.shareOnLinkedInLabel}
            />
          </div>
        </div>

        {props.ctaLabel && props.ctaHref && (
          <div
            className="flexbox-no-shrink margin-top-8 margin-horizontal-24 margin-bottom-24"
            css={hideOnShortScreenCss}
          >
            <ArticleCtaButton
              label={props.ctaLabel}
              href={props.ctaHref}
              fullWidth
              data-test-id="articleLayoutSidebarCta"
            />
          </div>
        )}
      </div>
    </aside>
  )
}
